/**
 * K8s resource quantity helpers.
 *
 * Converts between K8s resource strings (e.g., "500m", "512Mi", "2Gi")
 * and numeric values for UI display and state management.
 *
 * CPU is stored internally as cores (0.5, 1, 2) and displayed as K8s millicores
 * or whole cores. Memory is stored as MiB (512, 1024, 2048) and displayed as
 * K8s quantities (128Mi, 256Mi, 512Mi, 1Gi, etc.). Storage is stored as GiB
 * and displayed as Gi.
 */

// ── CPU ────────────────────────────────────────────────────────────────────────

/** Parse a K8s CPU quantity string to cores. e.g., "500m" → 0.5, "2" → 2 */
export function parseCpu(value: string): number {
  if (!value) return 0;
  if (value.endsWith("m")) return parseInt(value.slice(0, -1), 10) / 1000;
  return parseFloat(value);
}

/** Format a CPU value in cores to a K8s quantity string. e.g., 0.5 → "500m", 2 → "2" */
export function formatCpu(cores: number): string {
  if (cores === 0) return "0";
  // Use millicores for sub-core values, whole cores for integer values
  const millicores = Math.round(cores * 1000);
  if (millicores % 1000 === 0) return `${millicores / 1000}`;
  return `${millicores}m`;
}

/** Human-readable CPU label. e.g., 0.5 → "500m", 2 → "2 cores" */
export function cpuLabel(cores: number): string {
  if (cores >= 1 && cores === Math.floor(cores))
    return `${cores} core${cores !== 1 ? "s" : ""}`;
  return formatCpu(cores);
}

// ── Memory ──────────────────────────────────────────────────────────────────────

/** Parse a K8s memory quantity string to MiB. e.g., "512Mi" → 512, "1Gi" → 1024 */
export function parseMemory(value: string): number {
  if (!value) return 0;
  if (value.endsWith("Gi")) return Math.round(parseFloat(value) * 1024);
  if (value.endsWith("Mi")) return Math.round(parseFloat(value));
  if (value.endsWith("Ki")) return Math.round(parseFloat(value) / 1024);
  return Math.round(parseInt(value, 10) / (1024 * 1024));
}

/** Format a MiB value to a K8s quantity string. Uses Gi for ≥ 1024 MiB, Mi otherwise. */
export function formatMemory(mib: number): string {
  if (mib === 0) return "0";
  if (mib >= 1024 && mib % 1024 === 0) return `${mib / 1024}Gi`;
  return `${mib}Mi`;
}

/** Human-readable memory label. e.g., 512 → "512 Mi", 2048 → "2 Gi" */
export function memoryLabel(mib: number): string {
  if (mib >= 1024 && mib % 1024 === 0) return `${mib / 1024} Gi`;
  return `${mib} Mi`;
}

// ── Storage ──────────────────────────────────────────────────────────────────

/** Parse a K8s storage quantity string to GiB. e.g., "10Gi" → 10, "500Mi" → 0.5 */
export function formatStorage(gib: number): string {
  if (gib === 0) return "0";
  return `${gib}Gi`;
}

/** Human-readable storage label. e.g., 10 → "10 Gi" */
export function storageLabel(gib: number): string {
  return `${gib} Gi`;
}

// ── Tier presets ──────────────────────────────────────────────────────────────

export interface TierPreset {
  label: string;
  /** K8s quantity string */
  value: string;
  /** Internal numeric value (cores for CPU, MiB for memory, GiB for storage) */
  numeric: number;
}

export const CPU_TIERS: TierPreset[] = [
  { label: "Small", value: "250m", numeric: 0.25 },
  { label: "Medium", value: "500m", numeric: 0.5 },
  { label: "Large", value: "1", numeric: 1 },
  { label: "XL", value: "2", numeric: 2 },
];

export const CPU_TIERS_STATEFUL: TierPreset[] = [
  { label: "Small", value: "250m", numeric: 0.25 },
  { label: "Medium", value: "500m", numeric: 0.5 },
  { label: "Large", value: "1", numeric: 1 },
  { label: "XL", value: "4", numeric: 4 },
];

export const MEMORY_TIERS: TierPreset[] = [
  { label: "Small", value: "256Mi", numeric: 256 },
  { label: "Medium", value: "512Mi", numeric: 512 },
  { label: "Large", value: "1Gi", numeric: 1024 },
  { label: "XL", value: "2Gi", numeric: 2048 },
];

export const MEMORY_TIERS_STATEFUL: TierPreset[] = [
  { label: "Small", value: "256Mi", numeric: 256 },
  { label: "Medium", value: "512Mi", numeric: 512 },
  { label: "Large", value: "1Gi", numeric: 1024 },
  { label: "XL", value: "4Gi", numeric: 4096 },
];

export const STORAGE_TIERS: TierPreset[] = [
  { label: "Small", value: "1Gi", numeric: 1 },
  { label: "Medium", value: "5Gi", numeric: 5 },
  { label: "Large", value: "10Gi", numeric: 10 },
  { label: "XL", value: "20Gi", numeric: 20 },
];

/** Find which tier matches a numeric value, returns the tier label or "Custom" */
export function findTierLabel(tiers: TierPreset[], numeric: number): string {
  const match = tiers.find((t) => t.numeric === numeric);
  return match ? match.label : "Custom";
}
